import {
  Sidebar,
  SidebarDivider,
  SidebarGroup,
  SidebarItem,
  SidebarScrollWrapper,
  SidebarSpace,
  SidebarSubmenu,
  SidebarSubmenuItem,
} from '@backstage/core-components';
import {
  NavContentBlueprint,
  type NavContentComponentProps,
} from '@backstage/plugin-app-react';
import { useApp } from '@backstage/core-plugin-api';
import { SidebarLogo } from './SidebarLogo';
import MenuIcon from '@material-ui/icons/Menu';
import SearchIcon from '@material-ui/icons/Search';
import HomeIcon from '@material-ui/icons/Home';
import CategoryIcon from '@material-ui/icons/Category';
import LibraryBooks from '@material-ui/icons/LibraryBooks';
import LibraryBooksOutlined from '@material-ui/icons/LibraryBooksOutlined';
import CreateComponentIcon from '@material-ui/icons/AddCircleOutline';
import HelpIcon from '@material-ui/icons/Help';
import GroupIcon from '@material-ui/icons/People';
import OndemandVideoIcon from '@material-ui/icons/OndemandVideo';
import DockerIcon from '@material-ui/icons/Storage';
import HarnessIcon from '@material-ui/icons/Assessment';
import BuildIcon from '@material-ui/icons/Build';
import AnnouncementIcon from '@material-ui/icons/Announcement';
import RadarIcon from '@mui/icons-material/Radar';
import { SidebarSearchModal } from '@backstage/plugin-search';
import { UserSettingsSignInAvatar } from '@backstage/plugin-user-settings';
import { Settings as SidebarSettings } from '@backstage/plugin-user-settings';
import { SafeNotificationsSidebarItem } from './SafeNotificationsSidebarItem';
import { MyGroupsSidebarItem } from '@backstage/plugin-org';
import { SafeAdministration } from './SafeAdministration';
import { RequirePermission } from '@backstage/plugin-permission-react';
import {
  createMenuReadPermission,
  devtoolsReadPermission,
  techRadarReadPermission,
} from '../../nav/permissions';

const CatalogSubmenu = () => {
  const app = useApp();
  return (
    <SidebarSubmenu title="Catalog">
      <SidebarSubmenuItem
        title="Domains (Business IDs)"
        to="catalog?filters[kind]=domain"
        icon={app.getSystemIcon('kind:domain')}
      />
      <SidebarSubmenuItem
        title="Systems (Application IDs)"
        to="catalog?filters[kind]=system"
        icon={app.getSystemIcon('kind:system')}
      />
      <SidebarSubmenuItem
        title="Components"
        to="catalog?filters[kind]=component"
        icon={app.getSystemIcon('kind:component')}
      />
      <SidebarSubmenuItem
        title="APIs"
        to="api-docs"
        icon={app.getSystemIcon('kind:api')}
      />
      <SidebarDivider />
      <SidebarSubmenuItem
        title="Resources"
        to="catalog?filters[kind]=resource"
        icon={app.getSystemIcon('kind:resource')}
      />
      <SidebarDivider />
      <SidebarSubmenuItem
        title="Groups"
        to="catalog?filters[kind]=group"
        icon={app.getSystemIcon('kind:group')}
      />
      <SidebarSubmenuItem
        title="Users"
        to="catalog?filters[kind]=user"
        icon={app.getSystemIcon('kind:user')}
      />
    </SidebarSubmenu>
  );
};

export const SidebarComponent = ({ navItems }: NavContentComponentProps) => {
  const nav = navItems.withComponent(item => (
    <SidebarItem icon={() => item.icon} to={item.href} text={item.title} />
  ));

  // Items rendered explicitly below are consumed so they don't also appear
  // via nav.rest(). They may not yet exist (registered in Phase 5); take()
  // returns null in that case, which is harmless.
  nav.take('page:search'); // Using the search modal instead.
  nav.take('page:catalog'); // Rendered with a custom submenu below.
  // Community plugins wired in Phase 5d contribute their own nav items; the
  // sidebar already places these explicitly (Tech Radar, Announcements,
  // DevTools, Notifications, Administration/RBAC), so consume the auto items
  // to avoid duplicate sidebar entries.
  nav.take('page:tech-radar');
  nav.take('page:announcements');
  nav.take('page:rbac');
  nav.take('page:notifications');
  nav.take('page:devtools');
  // Core plugins whose nav items are also placed explicitly above (Docs in the
  // Menu group, Create..., APIs under the Catalog submenu, Settings as a group
  // label). Consume their auto items so they don't render a second time.
  nav.take('page:techdocs');
  nav.take('page:scaffolder');
  nav.take('page:api-docs');
  nav.take('page:user-settings');
  // The harness-check plugin is BUI-only and contributes no icon, so its auto
  // item renders as invisible text in the collapsed icon rail. Place it
  // explicitly below instead, with an icon, like Docker.
  nav.take('page:harness-check');

  return (
        <Sidebar data-testid="sidebar-root">
          <SidebarLogo />
          <SidebarGroup label="Search" icon={<SearchIcon />} to="/search">
            <SidebarSearchModal />
          </SidebarGroup>
          <SidebarItem
            icon={HelpIcon}
            to="docs/default/component/dev-platform-backstage"
            text="Help"
          />
          <SidebarDivider />
          <SidebarGroup label="Menu" icon={<MenuIcon />}>
            {/* Global nav, not org-specific */}
            <SidebarItem icon={HomeIcon} to="/" text="Home" />
            <SidebarItem icon={CategoryIcon} to="catalog" text="Catalog">
              <CatalogSubmenu />
            </SidebarItem>
            <MyGroupsSidebarItem
              singularTitle="My Group"
              pluralTitle="My Groups"
              icon={GroupIcon}
            />
            <SidebarItem icon={LibraryBooks} to="docs" text="Docs" />
            <RequirePermission
              permission={createMenuReadPermission}
              errorPage={<></>}
            >
              <SidebarItem
                icon={CreateComponentIcon}
                to="create"
                text="Create..."
              />
            </RequirePermission>
            {/* End global nav */}
            <SidebarDivider />
            <SidebarScrollWrapper>
              {/* Items in this group are scrollable if they run out of space */}
              <SidebarItem
                icon={OndemandVideoIcon}
                to="docs/default/component/cloud-learning-videos"
                text="Learning videos"
              />
              <SidebarItem
                icon={LibraryBooksOutlined}
                to="libver"
                text="JLL Libraries"
              />
              <RequirePermission
                permission={techRadarReadPermission}
                errorPage={<></>}
              >
                <SidebarItem
                  icon={RadarIcon}
                  to="tech-radar"
                  text="Tech Radar"
                />
              </RequirePermission>
              <SidebarItem icon={DockerIcon} to="docker" text="Docker" />
              <SidebarItem icon={HarnessIcon} to="harness" text="Harness" />
              {/* Pages registered in Phase 5 that aren't explicitly placed
                  above appear here. */}
              {nav.rest({ sortBy: 'title' })}
            </SidebarScrollWrapper>
          </SidebarGroup>
          <SidebarSpace />
          <SidebarDivider />
          <SafeNotificationsSidebarItem />
          <SidebarItem
            icon={AnnouncementIcon}
            to="announcements"
            text="Announcements"
          />
          <SidebarDivider />
          <SidebarGroup
            label="Settings"
            icon={<UserSettingsSignInAvatar />}
            to="/settings"
          >
            <SidebarSettings />
            <RequirePermission
              permission={devtoolsReadPermission}
              errorPage={<></>}
            >
              <SidebarItem icon={BuildIcon} to="devtools" text="DevTools" />
            </RequirePermission>
            <SafeAdministration />
          </SidebarGroup>
        </Sidebar>
      );
};

export const SidebarContent = NavContentBlueprint.make({
  params: {
    component: props => <SidebarComponent {...props} />,
  },
});
